/**
 * LinkedIn Sign-In button — server-side OIDC authorization code flow.
 * Clicking redirects to the backend `/auth/linkedin/start` which then forwards
 * to LinkedIn's OAuth authorize URL. LinkedIn redirects back to
 * `/auth/linkedin/callback` on the frontend, which POSTs code+state to
 * `/auth/linkedin/finish` and receives our JWT.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export const LinkedInSignInButton: React.FC<{ mode?: "signin" | "signup" }> = ({ mode = "signin" }) => {
  const [configured, setConfigured] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/linkedin/config")
      .then((r) => setConfigured(!!r.data.configured))
      .catch(() => {});
  }, []);

  if (!configured) return null;

  const click = () => {
    setBusy(true);
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const origin = window.location.origin;
    window.location.href = `${backend}/api/auth/linkedin/start?origin=${encodeURIComponent(origin)}`;
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      data-testid={`linkedin-signin-${mode}`}
      className="w-full h-10 flex items-center justify-center gap-2 rounded-full border border-[color:var(--sg-border)] bg-white dark:bg-[color:var(--sg-card)] text-[#0F172A] dark:text-[color:var(--sg-fg)] font-medium text-sm hover:border-[#0A66C2] transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
        <svg className="h-4 w-4" viewBox="0 0 32 32" fill="#0A66C2" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M27.3 27.3H22.6v-7.4c0-1.8 0-4-2.5-4-2.5 0-2.9 1.9-2.9 3.9v7.6h-4.7V12h4.5v2.1h.1c.6-1.2 2.1-2.5 4.4-2.5 4.7 0 5.6 3.1 5.6 7.1v8.6zM7.1 9.9a2.7 2.7 0 1 1 0-5.4 2.7 2.7 0 0 1 0 5.4zm-2.4 17.4h4.7V12H4.7v15.3zM29.2 0H2.8A2.8 2.8 0 0 0 0 2.7v26.5A2.8 2.8 0 0 0 2.8 32h26.4A2.8 2.8 0 0 0 32 29.2V2.8A2.8 2.8 0 0 0 29.2 0z"/>
        </svg>
      )}
      <span>{mode === "signup" ? "Sign up with LinkedIn" : "Continue with LinkedIn"}</span>
    </button>
  );
};

export default LinkedInSignInButton;
